import {Component, effect, inject, OnDestroy, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IStoreOverrideSettings, SettingsReferenceEnum} from '../../models';
import {
  selectFeatureGeneralSettings,
  selectSelectedSetting,
  selectSelectedStoreDefaultMaxItemLimit,
  selectSelectedStoreIncludeDelColDefault,
} from '../../store/setting.selectors';
import {
  ICrudOptions,
  IUserAccessFeatureStockTakeSettings,
} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockTakeSettingsForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {settingsSetSelected, settingStoreOverrides} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {
  clampBaseItemLimit,
  resolveStockTakeDefaultMaxItemLimit,
  STOCK_TAKE_BASE_ITEM_LIMIT,
  STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT,
} from '../../../stock-take/utils/stock-take-limits.utils';

@Component({
  selector: 'app-feature-stock-take-general',
  templateUrl: './feature-stock-take-general.component.html',
  styleUrl: './feature-stock-take-general.component.scss',
  standalone: false,
})
export class FeatureStockTakeGeneralComponent implements OnDestroy, IHasUnsavedChanges {

  private readonly _store = inject(Store);
  private readonly modalUtils = inject(SharedModalUtils);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  includeDelColDefault: boolean = true;
  defaultMaxItemLimit: number = STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly baseItemLimitMax = STOCK_TAKE_BASE_ITEM_LIMIT;
  protected readonly appDefaultMaxItemLimit = STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT;

  private readonly _includeDelColDefault: Signal<boolean> = this._store.selectSignal(selectSelectedStoreIncludeDelColDefault);
  private readonly _defaultMaxItemLimit: Signal<number> = this._store.selectSignal(selectSelectedStoreDefaultMaxItemLimit);
  private readonly _allOverrides = this._store.selectSignal(selectFeatureGeneralSettings);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _uaStockTake: Signal<IUserAccessFeatureStockTakeSettings> = this._store.selectSignal(
    selectUserAccessFeatureStockTakeSettingsForSelectedStore,
  );
  #hasOverridesBeenEdited: boolean = false;

  constructor() {
    effect(() => {
      const includeDelColDefault = this._includeDelColDefault();
      const defaultMaxItemLimit = this._defaultMaxItemLimit();
      const access = this._uaStockTake();
      this.initializeForms(access, includeDelColDefault, defaultMaxItemLimit);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });

    this.unsavedChangesService.registerComponent(this);
  }

  get hasOverridesBeenEdited(): boolean {
    return this.#hasOverridesBeenEdited;
  }

  get isOverridesSubmitDisabled(): boolean {
    return (this.canOverridesBeEdited === false) || (this.canOverridesBeEdited === true && this.hasOverridesBeenEdited === false);
  }

  get canOverridesBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canEdit ?? false;
  }

  get canOverridesBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canSubmit ?? false;
  }

  get canOverridesBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canView ?? false;
  }

  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettingsUserAccess(): IUserAccessFeatureStockTakeSettings {
    return this._uaStockTake();
  }

  get haveAnyEditsBeenMade(): boolean {
    return this.#hasOverridesBeenEdited;
  }

  get isSaveAllDisabled(): boolean {
    return !this.haveAnyEditsBeenMade || !this.canOverridesBeSubmitted;
  }

  get hasUnsavedChanges(): boolean {
    return this.haveAnyEditsBeenMade;
  }

  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
      case 'overrides':
      default:
        this.revertOverrides();
        break;
    }
  }

  onSubmitForm(_form: string): void {
    if (!this.canOverridesBeSubmitted) {
      return;
    }
    this.#hasOverridesBeenEdited = false;
    const storeId = this.selectedStore?.storeId;
    const existingOverrides: IStoreOverrideSettings = storeId
      ? (this._allOverrides()?.overrides?.[storeId] ?? {})
      : {};
    this._store.dispatch(settingStoreOverrides.setStoreOverride({
      overrides: {
        ...existingOverrides,
        includeDelColDefault: this.includeDelColDefault,
        defaultMaxItemLimit: clampBaseItemLimit(this.defaultMaxItemLimit),
      },
    }));
  }

  onToggleIncludeDelColDefault(toggleValue: boolean): void {
    if (this.includeDelColDefault !== toggleValue) {
      this.includeDelColDefault = toggleValue;
      this.#hasOverridesBeenEdited = true;
    }
  }

  onDefaultMaxItemLimitInput(event: Event): void {
    const parsed = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(parsed) || parsed < 1) {
      return;
    }
    const next = clampBaseItemLimit(parsed);
    if (this.defaultMaxItemLimit !== next) {
      this.defaultMaxItemLimit = next;
      this.#hasOverridesBeenEdited = true;
    }
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  private initializeForms(
    access: IUserAccessFeatureStockTakeSettings,
    includeDelColDefault: boolean,
    defaultMaxItemLimit: number,
  ): void {
    this.buildOverridesForm(access?.overrides, includeDelColDefault, defaultMaxItemLimit);
  }

  private buildOverridesForm(
    access: ICrudOptions | undefined,
    includeDelColDefault: boolean | undefined,
    defaultMaxItemLimit: number | undefined,
  ): void {
    if (access?.canView && this.hasOverridesBeenEdited === false) {
      this.includeDelColDefault = includeDelColDefault !== false;
      this.defaultMaxItemLimit = resolveStockTakeDefaultMaxItemLimit(defaultMaxItemLimit);
    }
  }

  private revertOverrides(): void {
    this.includeDelColDefault = this._includeDelColDefault();
    this.defaultMaxItemLimit = this._defaultMaxItemLimit();
    this.#hasOverridesBeenEdited = false;
  }

  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    if (!this.haveAnyEditsBeenMade) {
      return true;
    }
    const modalConfig: ISharedModalBasic = {
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: 'Unsaved Changes',
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      buttonAcceptText: 'Discard Changes & Continue',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalConfig);
    if (result.role === 'modal-accept') {
      this.onRevertForm('all');
      return true;
    }
    this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.featureStockTakeGeneral}));
    return false;
  }
}
